// Todo: cleanup store, remove unused methods and flow
// From now, there is no separate list of all jobs, just jobs per order.
// That means, all stuff, i.e. pagination, search, etc. should be moved to context of orderID, or to order store itself

import { uniqBy } from 'lodash-es';
import { makeObservable, observable, runInAction } from 'mobx';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Job, JobAssignment } from '~hooks/useJob/models';
import { User } from '~hooks/useUsers/models';
import { Pagination, PaginationLink } from '~services/pagination';
import { RootStore } from '~store/RootStore';
import { getPageNumber } from '~utils/pagination';

class JobStore {
  jobs: Job[] = [];
  batchedJobs: Job[] = [];
  companyDrivers: Record<string, User[]> = {};
  accountDrivers: Record<string, User[]> = {};
  jobAssignments: Record<string, JobAssignment[]> = {};
  jobFilter: Record<string, any> = {};
  dispatchFilter: Record<string, any> = {};
  areFilterOptionsLoaded: boolean = false;

  // Todo: move to order store ???
  pagination: Pagination = {
    limit: MAX_API_PAGE_SIZE,
    after: '',
    before: '',
    page: 1,
  };

  constructor(private readonly rootStore: RootStore) {
    makeObservable(this, {
      jobs: observable,
      batchedJobs: observable,
      pagination: observable,
      companyDrivers: observable,
      accountDrivers: observable,
      jobAssignments: observable,
      jobFilter: observable,
      dispatchFilter: observable,
      areFilterOptionsLoaded: observable,
    });
    this.rootStore = rootStore;
  }

  public get driversByCompany() {
    return (companyId: string) => {
      const list = uniqBy(
        [
          ...(this.companyDrivers[companyId] || []),
          ...(this.accountDrivers[companyId] || []),
        ],
        'id',
      );
      return list.length ? list : undefined;
    };
  }

  public get jobsByOrder() {
    return (orderId: string) => {
      return this.jobs.filter((job) => job.order?.id === orderId) || [];
    };
  }
  public get jobById() {
    return (jobId: string) => this.jobs.find((job) => job.id === jobId) || null;
  }

  public get getAllCompanyDrivers() {
    return () => {
      const list = uniqBy([...(Object.values(this.companyDrivers) || [])], 'id');
      return list.length ? list : null;
    };
  }

  setPagination(pagination: Pagination) {
    this.pagination = {
      ...this.pagination,
      before: pagination.before || '',
      after: pagination.after || '',
    };
  }

  updatePageNumber(link: PaginationLink) {
    runInAction(() => {
      this.pagination = {
        ...this.pagination,
        page: getPageNumber(this.pagination.page, link),
      };
    });
  }

  setJobs(jobs: Job[]) {
    this.jobs = uniqBy(jobs, 'id');
  }

  setBatchedJobs(jobs: Job[]) {
    const allJobs = [...this.batchedJobs, ...jobs];

    this.batchedJobs = uniqBy(allJobs, 'id');
  }

  /**
   * Sets the jobs for a specific order ID.
   * Filters out the jobs that are not part of the order so that the list is updated
   * based on any changes made by searching or filtering.
   *
   * @param orderId - The ID of the order.
   * @param jobs - An array of Job objects.
   */
  setJobsByOrder(orderId: string, jobs: Job[]) {
    const prevJobs = this.jobs.filter((job) => job.order?.id !== orderId);
    this.jobs = uniqBy([...prevJobs, ...jobs], 'id');
  }

  addJob(job: Job) {
    const idx = this.jobs.findIndex((j) => j.id === job.id);
    if (idx !== -1) {
      this.updateJob(job);
    } else {
      this.jobs = uniqBy([...this.jobs, job], 'id');
    }
  }

  addJobs(jobs: Job[]) {
    this.jobs = uniqBy([...jobs, ...this.jobs], 'id');
  }

  removeJob(id: string) {
    this.jobs = this.jobs.filter((job) => job.id !== id);
  }

  updateJob(job: Job) {
    this.jobs = this.jobs.map((j) => (j.id === job.id ? job : j));
  }

  setAccountDrivers = (companyId: string, drivers: User[]) => {
    this.accountDrivers[companyId] = drivers;
  };

  setDrivers = (companyId: string, drivers: User[]) => {
    this.companyDrivers[companyId] = drivers;
  };

  setJobAssignments = (jobId: string, jobAssignments: JobAssignment[]) => {
    this.jobAssignments[jobId] = jobAssignments;
  };

  setJobFilter(filter: Record<string, any>) {
    this.jobFilter = filter;
  }

  addJobFilter(filter: Record<string, any>) {
    this.jobFilter = {
      ...this.jobFilter,
      ...filter,
    };
  }

  setDispatchFilter(filter: Record<string, any>) {
    this.dispatchFilter = filter;
  }

  addDispatchFilter(filter: Record<string, any>) {
    this.dispatchFilter = {
      ...this.dispatchFilter,
      ...filter,
    };
  }

  setFilterOptionsLoaded = (loaded: boolean) => {
    this.areFilterOptionsLoaded = loaded;
  };
}
export default JobStore;
